import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, Loader2, User, Mail, Lock } from "lucide-react";
import { BrandMark } from "@/components/Brand";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (res.ok) { toast.success("Account created"); nav("/portal"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen relative grain flex items-center justify-center p-6 bg-ink overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand/20 blur-[120px]" />
      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-2xl bg-brand flex items-center justify-center brand-glow">
            <Heart className="w-6 h-6 text-white" fill="white" />
          </div>
          <span className="text-2xl"><BrandMark className="text-xl" /></span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Begin your<br />connection.
        </h1>
        <p className="text-muted-foreground mt-4 mb-8 leading-relaxed">
          Create an account and design your companion.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="register-name-input" placeholder="Your name"
              value={name} onChange={(e) => setName(e.target.value)}
              className="h-14 pl-11 bg-surface border-white/10 rounded-2xl" />
          </div>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="register-email-input" type="email" required placeholder="Email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-14 pl-11 bg-surface border-white/10 rounded-2xl" />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="register-password-input" type="password" required placeholder="Password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-14 pl-11 bg-surface border-white/10 rounded-2xl" />
          </div>
          <Button data-testid="register-submit-button" disabled={loading}
            className="w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white font-semibold text-base transition-colors brand-glow">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6">
          Already have one?{" "}
          <Link to="/login" data-testid="go-login-link" className="text-brand hover:text-brand-hover font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
